import React from "react";
import { useAuth } from "../../core/hooks/useAuth";
import { useFlexBenefitsFeatureFlag } from "../../core/hooks/useFlexBenefitsFeatureFlag";
import { FlexPackage } from "../../domain/entity/FlexPackage";
import { flexBenefitsProvidedByText } from "../../core/constants/strings";

// A stable reference, not `[]` inline below: `IdCardDropdown` depends on this return
// value inside a useEffect dep array, and a fresh `[]` on every render (the common
// case -- nearly every member has the flag disabled or no packages) makes that effect
// refire every render forever. Module-level so every render of every consumer shares it.
const NO_PACKAGES: FlexPackage[] = [];

// The PolicyType value pivot-health-mobile-api's PolicyType.java enum uses for
// FlexBenefits (Slice 4, PPI-1391). Shared here so IdCardDropdown/IdCardDownloadButton
// don't each hardcode their own copy of the literal.
export const FLEX_BENEFITS_PRODUCT_TYPE = "FLEX_BENEFITS";

/**
 * FlexBenefits in the sidebar, matching the approved mock.
 *
 * The plan-list row itself is FlexBenefitsProductDropdown now (Slice 2) — one per
 * package, rendered by AddOnsInformation, replacing this file's own Slice-1-only
 * plain row once there was real per-product data to expand into. This file keeps the
 * administrator sentence (below the whole list, sibling to the "**Date reflects
 * expiration" disclaimer) and the shared useFlexPackages() visibility ladder both
 * consume. Effective date and application id are NOT here: the mock shows those on
 * the document rows.
 *
 * No status either: external_application.status_type has no documented meaning, so an
 * "Active" badge would assert coverage that cannot be verified. Nothing in this file
 * or FlexBenefitsProductDropdown renders one.
 */

/**
 * Shared visibility ladder, so both exports appear and disappear together.
 *
 * isLoading is only true when there's something worth waiting for: the application
 * itself hasn't loaded yet, or it HAS packages and the experiment gate hasn't resolved.
 * A member with zero packages must resolve to isLoading: false immediately — otherwise
 * every member without FlexBenefits (nearly everyone) sees a skeleton flash while the
 * flag loads, for a feature that was never going to show them anything.
 *
 * Exported so FlexBenefitsSupport shares this exact derivation instead of
 * re-implementing it against auth/flag directly.
 */
export const useFlexPackages = () => {
  const auth = useAuth();
  const flag = useFlexBenefitsFeatureFlag();
  const rawPackages = auth.application?.flexPackages ?? NO_PACKAGES;
  return {
    isLoading:
      auth.gettingApplication || (rawPackages.length > 0 && flag.isLoading),
    packages: flag.enabled ? rawPackages : NO_PACKAGES,
  };
};

/**
 * The person the coverage covers, when FlexBenefits is ALL the member holds
 * (PPI-1391 QA). A FlexBenefits standalone response comes back with an empty
 * `apps` array, so `application.members` is empty and every name label in the
 * sidebar renders blank -- the covered person's identity only exists on the
 * external plan itself.
 *
 * Deliberately NOT behind useFlexPackages()' feature-flag ladder, unlike every
 * other consumer in this file. The flag gates the FlexBenefits *plan UI*; it does
 * not gate who the logged-in member is. Reading it through the ladder meant a
 * standalone member whose flag was off saw a nameless sidebar -- their own name
 * withheld by a flag about something else entirely -- and the name reveals nothing
 * about FlexBenefits either way. `isLoading` is therefore just the application's.
 *
 * Takes the first package that has one: every package in a standalone response
 * belongs to the same logged-in member, so they all name the same person.
 */
export const useFlexMember = () => {
  const auth = useAuth();
  const packages = auth.application?.flexPackages ?? NO_PACKAGES;
  return {
    isLoading: auth.gettingApplication,
    member: packages.find((it) => it.member)?.member,
  };
};

/**
 * Collapses enrollment records down to one per distinct package. `id` is the
 * package-type identity FlexBenefitsProductDropdown fetches products by, not a
 * per-enrollment id, so a member holding many enrollments of the *same* package
 * (PPI-1740 at its most extreme -- one real QA account has 25 enrollments across
 * only 2 distinct packages) would otherwise repeat that one package's name or
 * fallback label once per enrollment. Falls back to `name` only when `id` itself
 * is missing, and drops a record entirely once neither is present -- nothing
 * left to key on or show. Prefers a named record as the kept representative when
 * duplicates disagree on whether a name is present.
 */
export function dedupePackagesByIdentity(
  packages: FlexPackage[]
): FlexPackage[] {
  const byKey = new Map<string, FlexPackage>();

  packages.forEach((pkg) => {
    const key = pkg.id ?? pkg.name;
    if (key === undefined) {
      return;
    }
    const existing = byKey.get(key);
    if (!existing || (!existing.name && pkg.name)) {
      byKey.set(key, pkg);
    }
  });

  return [...byKey.values()];
}

/** The administrator sentence. Rendered by Sidebar, below the plan list. */
export const FlexBenefitsProvidedBy = () => {
  const { isLoading, packages } = useFlexPackages();

  if (isLoading || packages.length === 0) {
    return null;
  }

  // One member can hold several packages (PPI-1740). The mock shows a single group row,
  // so the names are joined into the one sentence rather than repeating the row.
  const names = dedupePackagesByIdentity(packages)
    .map((it) => it.name)
    .filter(Boolean);

  if (names.length === 0) {
    return null;
  }

  const [before, after] = flexBenefitsProvidedByText.split("{package}");

  return (
    <p className="silver-text mb-0" id="flexbenefits-provided-by">
      {before}
      <strong className="font-weight-bold">{names.join(", ")}</strong>
      {after}
    </p>
  );
};
